import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import {
  ExternalTransfer,
  useGetExternalTransfers,
} from "api/transfers/useGetExternalTransfers";
import { CryptoLogo } from "components/CryptoLogo/CryptoLogo";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContactIdData } from "providers/ContactIdProvider";
import { BiBell } from "react-icons/bi";
import { CgArrowLeft } from "react-icons/cg";

import { useParams, useNavigate } from "react-router-dom";

const NotificationButton = () => {
  const { selectedContactId } = useGetContactIdData();
  const { data: { portfolios } = { portfolios: [] } } = useGetContactInfo(
    false,
    selectedContactId
  );
  const { portfolioId: portfolioIdFromUrl } = useParams() || "";
  const portfolioId = Number(portfolioIdFromUrl);
  const { t } = useModifiedTranslation();
  const navigate = useNavigate();

  const foundPortfolio = portfolios.find(
    (portfolio) => portfolio.id === portfolioId
  );

  const { externalTransfers, loading: transfersLoading } =
    useGetExternalTransfers(
      selectedContactId ? selectedContactId.toString() : undefined
    );

  const filteredTransfers = externalTransfers.filter(
    (transfer) =>
      transfer.status === "CREATED" &&
      (!foundPortfolio?.shortName ||
        transfer.portfolioShortName === foundPortfolio?.shortName)
  );

  /**
   * Navigate the user to the Transfers view with the crypto – deposit tab
   * pre-selected so that the DepositSecuritiesCard (step 4) is visible.
   */
  const handleUpdateInfo = (transfer: ExternalTransfer) => {
    if (!portfolioId) return;

    // Close the dropdown before navigating
    // (Headless UI closes automatically when Menu.Button loses focus)

    navigate(
      `/portfolio/${portfolioId}/transfers?currencyType=crypto&transferType=deposit&extId=${transfer.extId}`
    );
  };

  return (
    <>
      <Menu as="div" className="flex relative items-center mx-2 sm:mx-0">
        <Menu.Button className="relative">
          <BiBell color="white" size={20} />
          {filteredTransfers.length > 0 && (
            <div className="flex absolute top-0 right-0 justify-center items-center text-white bg-red-500 rounded-full border border-white translate-x-1/3 -translate-y-1/3 w-[18px] h-[18px] text-[10px]">
              <span>
                {filteredTransfers.length > 9 ? "9+" : filteredTransfers.length}
              </span>
            </div>
          )}
        </Menu.Button>

        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
          as={Fragment}
        >
          <Menu.Items className="overflow-auto sm:overflow-visible fixed sm:absolute sm:before:absolute inset-0 sm:inset-auto sm:top-full sm:left-1/2 sm:before:left-1/2 z-10 sm:mt-3 sm:before:w-3 sm:before:h-3 bg-white sm:before:bg-white sm:rounded-2xl sm:ring-1 sm:ring-black sm:ring-opacity-5 sm:shadow-lg sm:before:transform sm:origin-top sm:before:rotate-45 sm:before:-translate-x-1/2 sm:-translate-x-1/2 focus:outline-none sm:before:content-[''] sm:before:top-[-6px] sm:min-w-[400px]">
            <div className="flex flex-col p-4 sm:p-2 h-full sm:h-auto">
              <div className="relative">
                <h3 className="mb-4 sm:mb-2 text-3xl font-semibold text-center text-kvarn-dark-green">
                  {t("notificationsMenu.notifications")}
                </h3>
                <Menu.Button className="sm:hidden absolute top-0 right-0 p-2">
                  <CgArrowLeft size={24} className="text-kvarn-dark-green" />
                </Menu.Button>
              </div>
              <div className="flex overflow-auto flex-col flex-grow rounded-2xl sm:max-h-[500px]">
                {filteredTransfers.length === 0 ? (
                  <div className="flex justify-center items-center h-full text-gray-500">
                    <p>
                      {transfersLoading
                        ? t("commin.loading")
                        : t("notificationsMenu.noNotifications")}
                    </p>
                  </div>
                ) : (
                  filteredTransfers.map((transfer) => (
                    <ExtTransferNotification
                      key={transfer.extId}
                      transfer={transfer}
                      handleUpdateInfo={() => handleUpdateInfo(transfer)}
                    />
                  ))
                )}
              </div>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  );
};

const ExtTransferNotification = ({
  transfer,
  handleUpdateInfo,
}: {
  transfer: ExternalTransfer;
  handleUpdateInfo: () => void;
}) => {
  const { t } = useModifiedTranslation();

  return (
    <div className="flex gap-4 py-3 px-4 bg-darker-green border-b border-white bg-opacity-[8%]">
      <div className="flex items-center">
        <CryptoLogo name={transfer.securityCode} className="w-6 h-6" />
      </div>
      <div className="flex flex-col flex-1 gap-1">
        <div className="flex justify-between font-semibold uppercase text-[10px]">
          <div>{t("notificationsMenu.actionRequired")}</div>
          <div>{new Date(transfer.createdAt).toLocaleDateString()}</div>
        </div>
        <div className="flex justify-between items-center text-sm">
          {t("notificationsMenu.updateInfoDescription")}
          <button
            onClick={handleUpdateInfo}
            className="py-0.5 px-1 text-xs text-white bg-kvarn-dark-green rounded hover:shadow-sm transition-all duration-150 ease-in-out hover:bg-kvarn-dark-green/90"
          >
            {t("notificationsMenu.updateInfoButtonText")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationButton;
